import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from './cx';

export type SortDirection = 'asc' | 'desc';

export interface TableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T, index: number) => ReactNode;
  /** Column can be sorted server-side; the header becomes a button and the <th> carries aria-sort. */
  sortable?: boolean;
  numeric?: boolean;
  className?: string;
  /** Render the cell as <th scope="row">. */
  rowHeader?: boolean;
  /** Width of the column, e.g. "38%" (set on its header cell). */
  width?: string;
}

export interface TableProps<T> {
  caption: ReactNode;
  captionClassName?: string;
  columns: TableColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  sort?: { key: string; direction: SortDirection } | null;
  onSort?: (key: string) => void;
  emptyText?: ReactNode;
  className?: string;
}

/** govuk/components/table with <caption>, <th scope> and optional sortable headers (button + aria-sort). */
export function Table<T>({ caption, captionClassName, columns, rows, rowKey, sort, onSort, emptyText, className }: TableProps<T>) {
  const { t } = useTranslation();
  const captionId = useId();
  const container = useRef<HTMLDivElement>(null);
  const scrollable = useHorizontalOverflow(container);
  return (
    // Only when the table overflows (narrow screens / zoom) the container becomes a focusable, labelled region,
    // so keyboard users can scroll it (WCAG 2.1.1, 1.4.10).
    <div
      className="app-table-container"
      ref={container}
      role={scrollable ? 'region' : undefined}
      aria-labelledby={scrollable ? captionId : undefined}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- set together with role="region"
      tabIndex={scrollable ? 0 : undefined}
    >
      <table className={cx('govuk-table', className)}>
        <caption id={captionId} className={cx('govuk-table__caption', captionClassName ?? 'govuk-table__caption--m')}>
          {caption}
        </caption>
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            {columns.map((col) => {
              const active = sort?.key === col.key;
              const ariaSort = col.sortable && onSort ? (active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : 'none') : undefined;
              return (
                <th
                  key={col.key}
                  scope="col"
                  className={cx('govuk-table__header', col.numeric && 'govuk-table__header--numeric', col.className)}
                  style={col.width ? { width: col.width } : undefined}
                  aria-sort={ariaSort}
                >
                  {col.sortable && onSort ? (
                    <button type="button" className="app-sort-button" onClick={() => onSort(col.key)}>
                      {col.header}
                      <span className="app-sort-button__icon" aria-hidden="true">
                        {active ? (sort!.direction === 'asc' ? '▲' : '▼') : '⇅'}
                      </span>
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          {rows.length === 0 && (
            <tr className="govuk-table__row">
              <td className="govuk-table__cell" colSpan={columns.length}>
                {emptyText ?? t('table.empty')}
              </td>
            </tr>
          )}
          {rows.map((row, i) => (
            <tr className="govuk-table__row" key={rowKey(row, i)}>
              {columns.map((col) =>
                col.rowHeader ? (
                  <th key={col.key} scope="row" className={cx('govuk-table__header', col.className)}>
                    {col.render(row, i)}
                  </th>
                ) : (
                  <td key={col.key} className={cx('govuk-table__cell', col.numeric && 'govuk-table__cell--numeric', col.className)}>
                    {col.render(row, i)}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function useHorizontalOverflow(ref: React.RefObject<HTMLElement | null>): boolean {
  const [overflow, setOverflow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => setOverflow(el.scrollWidth > el.clientWidth + 1);
    const observer = new ResizeObserver(update);
    observer.observe(el);
    const table = el.firstElementChild;
    if (table) observer.observe(table);
    update();
    return () => observer.disconnect();
  }, [ref]);
  return overflow;
}
